import { useState } from 'react'
import { Ban, CalendarClock, FileEdit, Lock, Rocket, ShieldCheck, StopCircle } from 'lucide-react'
import { Modal } from '../admin/primitives.js'
import { Checkbox } from '../admin/form.js'
import { useConfirm } from '../shell/confirm.js'
import { gesteSoiree } from './soirees-maison-api.js'
import {
  CANAL_LIBELLE, CANAUX, INSTANT_RX, PARIS_RX,
  consequences, consequencesArret, demandeIdentite, etatBouton, lectureSeule, libelleChoixHeure, parisLisible, succesArret, tonStatut, tuileMiseEnVente,
  type Canal, type ErreurBM, type Geste, type NomGeste, type StatutSoiree,
} from './soirees-maison-model.js'
import type { Ecrivain } from './SoireeMaisonPage.js'

/**
 * LES GESTES DE VENTE D'UNE SOIRÉE — mettre en vente, programmer, remettre en
 * brouillon, arrêter les ventes, annuler (contrat `soirees-v1` §4.7, §4.8).
 *
 * Tous demandent le droit de mise en vente et une CONFIRMATION D'IDENTITÉ :
 * `adminMutate` ouvre le dialogue quand le serveur la réclame, puis rejoue la
 * demande à l'identique (même clé). Chaque confirmation dit d'abord ce que le
 * geste fait, puis ce qui deviendra impossible ensuite, puis les
 * avertissements de la billetterie.
 *
 * Seuls les gestes qui ont un sens pour l'état de la soirée sont montrés ; un
 * geste montré mais refusé reste visible, grisé, avec sa raison.
 *
 * Le panneau vit dans la colonne de droite de la fiche : l'état de la vente
 * en tête (ce qui se vend, depuis quand), puis les gestes l'un sous l'autre,
 * le principal d'abord.
 */

const ICONE_ETAT: Record<StatutSoiree, typeof Rocket> = {
  BROUILLON: FileEdit, PROGRAMMEE: CalendarClock, EN_VENTE: Rocket, CLOTUREE: Lock, ANNULEE: Ban,
}

/** L'état de la vente, en tête du panneau : le même que la tuile « Mise en vente » du bandeau, en plus parlant. */
function EtatVente({ statut, valeur, aide }: { statut: StatutSoiree; valeur: string; aide: string }) {
  const Icone = ICONE_ETAT[statut]
  return (
    <div className={`bs-vente-etat bs-vente-etat--${tonStatut(statut)}`}>
      <span className="bs-vente-etat__icone" aria-hidden="true"><Icone size={18} /></span>
      <span className="bs-vente-etat__textes">
        <strong className="bs-vente-etat__valeur">{valeur}</strong>
        <span className="bs-vente-etat__aide">{aide}</span>
      </span>
    </div>
  )
}

function details(lignes: string[], geste: Geste): { label: string; value: string }[] {
  return lignes.map((t) => ({ label: geste.avertissements.some((a) => a.message === t) ? 'Attention' : 'Ensuite', value: t }))
}

export function FicheGestes({ ecrivain }: { ecrivain: Ecrivain }) {
  const confirm = useConfirm()
  const { soiree: s, fiche } = ecrivain
  const [ouvert, setOuvertBrut] = useState<'programmer' | 'annuler' | 'arreter' | null>(null)
  // Chaque ouverture repart d'un formulaire neuf ; la modale reste montée le temps de sa sortie.
  const [tour, setTour] = useState(0)
  const setOuvert = (o: 'programmer' | 'annuler' | 'arreter' | null) => { if (o) setTour((n) => n + 1); setOuvertBrut(o) }
  const fermer = () => setOuvertBrut(null)
  const [envoi, setEnvoi] = useState(false)
  const vente = tuileMiseEnVente(s)
  if (lectureSeule(fiche.actions)) {
    return (
      <section className="lc-panel bs-gestes" aria-labelledby="bs-gestes-titre">
        <header className="lc-panel__head"><h2 className="lc-section-title" id="bs-gestes-titre">Mise en vente</h2></header>
        <div className="lc-panel__body bs-gestes__corps">
          <EtatVente statut={s.statut} valeur={vente.valeur} aide={vente.aide} />
          <p className="lc-hint bs-lecture-seule">Lecture seule : vous voyez cette soirée, sans pouvoir la préparer ni la mettre en vente.</p>
        </div>
      </section>
    )
  }

  const etat = (nom: NomGeste, g: Geste) => etatBouton(nom, g, fiche.actions, fiche.ecriture)
  const g = s.gestes
  const enCours = s.statut === 'BROUILLON' || s.statut === 'PROGRAMMEE'
  const enLigne = s.statut === 'PROGRAMMEE' || s.statut === 'EN_VENTE'
  const finie = s.statut === 'ANNULEE' || s.statut === 'CLOTUREE'

  const lancer = async (geste: 'publier' | 'brouillon', corps: Record<string, unknown>, succes: string) => {
    setEnvoi(true)
    try {
      const e = await ecrivain.executer({ ecran: `soiree:${geste}`, corps, envoi: (cle) => gesteSoiree(s.id, geste, corps, cle), succes })
      ecrivain.signalerErreur(e)
    } finally { setEnvoi(false) }
  }

  const publier = async () => {
    const [premier, ...reste] = consequences('publier', s, g.publier)
    if (!(await confirm({
      title: `Mettre « ${s.nom} » en vente maintenant ?`, message: premier ?? '', details: details(reste, g.publier),
      variant: 'sensitive', confirmLabel: 'Mettre en vente',
    }))) return
    await lancer('publier', { version: s.version }, 'Soirée mise en vente.')
  }

  const brouillon = async () => {
    const [premier, ...reste] = consequences('brouillon', s, g.brouillon)
    if (!(await confirm({
      title: `Remettre « ${s.nom} » en brouillon ?`, message: premier ?? '', details: details(reste, g.brouillon),
      variant: 'sensitive', confirmLabel: 'Remettre en brouillon',
    }))) return
    await lancer('brouillon', { version: s.version }, 'Soirée remise en brouillon : plus rien ne se vend.')
  }

  // La confirmation d'identité se dit UNE fois, sous les gestes (maquette du 23/10/2026), pas sous chacun.
  let identite = false
  const bouton = (nom: NomGeste, geste: Geste, label: string, icone: React.ReactNode, onClick: () => void, danger = false) => {
    const e = etat(nom, geste)
    if (!e.raison && demandeIdentite(nom, geste)) identite = true
    return (
      <div className="bs-geste">
        <button type="button" className={`lc-btn${danger ? ' lc-btn--danger-text' : e.actif && nom === 'publier' ? ' lc-btn--primary' : ''}`}
          disabled={!e.actif || envoi} onClick={onClick}>
          {icone} {label}
        </button>
        {e.raison ? <span className="lc-hint bs-geste__raison">{e.raison}</span> : null}
      </div>
    )
  }

  return (
    <section className="lc-panel bs-gestes" aria-labelledby="bs-gestes-titre">
      <header className="lc-panel__head"><h2 className="lc-section-title" id="bs-gestes-titre">Mise en vente</h2></header>
      <div className="lc-panel__body bs-gestes__corps">
        <EtatVente statut={s.statut} valeur={vente.valeur} aide={vente.aide} />
        {finie ? <p className="lc-hint">{s.statut === 'ANNULEE' ? 'Soirée annulée : plus rien ne se vend, et elle ne se modifie plus.' : 'Soirée clôturée : plus rien ne se vend, et elle ne se modifie plus. Ses billets s’annulent encore depuis la page de secours.'}</p> : null}
        <div className="bs-gestes__liste">
          {enCours ? bouton('publier', g.publier, 'Mettre en vente', <Rocket size={15} aria-hidden="true" />, () => { void publier() }) : null}
          {enCours ? bouton('programmer', g.programmer, s.statut === 'PROGRAMMEE' ? 'Changer l’heure de mise en vente' : 'Programmer la mise en vente', <CalendarClock size={15} aria-hidden="true" />, () => setOuvert('programmer')) : null}
          {enLigne ? bouton('brouillon', g.brouillon, 'Remettre en brouillon', <FileEdit size={15} aria-hidden="true" />, () => { void brouillon() }) : null}
          {s.statut === 'EN_VENTE' ? bouton('arreter_ventes', g.arreter_ventes, 'Arrêter les ventes', <StopCircle size={15} aria-hidden="true" />, () => setOuvert('arreter')) : null}
          {!finie ? bouton('annuler', g.annuler, 'Annuler la soirée', <Ban size={15} aria-hidden="true" />, () => setOuvert('annuler'), true) : null}
        </div>
        {identite ? <p className="lc-hint bs-identite"><ShieldCheck size={13} aria-hidden="true" /> Chacun de ces gestes demande de confirmer votre identité.</p> : null}
      </div>
      <Modal open={ouvert === 'programmer'} onClose={fermer} ariaLabel="Programmer la mise en vente" width={560}>
        <Programmer key={`p${tour}`} ecrivain={ecrivain} onClose={fermer} />
      </Modal>
      <Modal open={ouvert === 'annuler'} onClose={fermer} ariaLabel="Annuler la soirée" width={560}>
        <Annuler key={`a${tour}`} ecrivain={ecrivain} onClose={fermer} />
      </Modal>
      <Modal open={ouvert === 'arreter'} onClose={fermer} ariaLabel="Arrêter les ventes" width={600}>
        <Arreter key={`r${tour}`} ecrivain={ecrivain} onClose={fermer} />
      </Modal>
    </section>
  )
}

/* ═══════════════════════ programmer ═══════════════════════ */

function Programmer({ ecrivain, onClose }: { ecrivain: Ecrivain; onClose: () => void }) {
  const s = ecrivain.soiree
  const [a, setA] = useState(s.mise_en_vente_paris ?? '')
  const [envoi, setEnvoi] = useState(false)
  const [erreur, setErreur] = useState<ErreurBM | null>(null)
  const lignes = consequences('programmer', s, s.gestes.programmer)
  const valide = PARIS_RX.test(a) || INSTANT_RX.test(a)

  const envoyer = async (valeur: string) => {
    setEnvoi(true); setErreur(null)
    const corps = { version: s.version, a: valeur }
    try {
      const e = await ecrivain.executer({
        ecran: 'soiree:programmer', corps, envoi: (cle) => gesteSoiree(s.id, 'programmer', corps, cle),
        succes: `Mise en vente programmée le ${parisLisible(valeur)}.`,
      })
      if (e) setErreur(e); else onClose()
    } finally { setEnvoi(false) }
  }
  const ambiguite = erreur?.genre === 'heure_ambigue' ? erreur.ambiguites[0] ?? null : null

  return (
    <div className="bs-modale">
      <h2 className="bs-modale__titre">Programmer la mise en vente</h2>
      <p className="lc-hint">« {s.nom} » passera seule en vente à l’heure choisie (heure de Paris).</p>
      <div className="lc-field">
        <label className="lc-label" htmlFor="bs-programmer-a">Jour et heure de la mise en vente</label>
        <input id="bs-programmer-a" className="lc-input" type="datetime-local" step={60} value={a.slice(0, 16)}
          onChange={(e) => { setA(e.target.value); setErreur(null) }} />
        <span className="lc-helper">Avant la fin de la soirée. La billetterie vérifie que l’heure n’est pas passée.</span>
      </div>
      <ul className="bs-consequences">{lignes.map((l) => <li key={l}>{l}</li>)}</ul>
      {ambiguite ? (
        <div className="bs-choix" role="group" aria-label="Préciser l’heure">
          <p>{erreur?.message}</p>
          <div className="bs-choix__boutons">
            {ambiguite.possibles.map((p) => (
              <button key={p} type="button" className="lc-btn" disabled={envoi} onClick={() => { setA(p); void envoyer(p) }}>{libelleChoixHeure(p)}</button>
            ))}
          </div>
        </div>
      ) : erreur ? <p className="bs-note bs-note--ko" role="alert">{erreur.message}</p> : null}
      <p className="lc-hint bs-identite"><ShieldCheck size={13} aria-hidden="true" /> Gestion vous demandera de confirmer votre identité.</p>
      <div className="bs-modale__actions">
        <button type="button" className="lc-btn lc-btn--ghost" onClick={onClose} disabled={envoi}>Fermer</button>
        <button type="button" className="lc-btn lc-btn--primary" disabled={!valide || envoi} onClick={() => { void envoyer(a) }}>
          {envoi ? 'Envoi…' : 'Programmer la mise en vente'}
        </button>
      </div>
    </div>
  )
}

/* ═══════════════════════ annuler ═══════════════════════ */

function Annuler({ ecrivain, onClose }: { ecrivain: Ecrivain; onClose: () => void }) {
  const s = ecrivain.soiree
  const [motif, setMotif] = useState('')
  const [envoi, setEnvoi] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)
  const lignes = consequences('annuler', s, s.gestes.annuler)
  const m = motif.trim()
  const valide = m.length >= 3 && m.length <= 500

  const envoyer = async () => {
    setEnvoi(true); setErreur(null)
    const corps = { version: s.version, motif: m }
    try {
      const e = await ecrivain.executer({ ecran: 'soiree:annuler', corps, envoi: (cle) => gesteSoiree(s.id, 'annuler', corps, cle), succes: 'Soirée annulée.' })
      if (e) setErreur(e.message); else onClose()
    } finally { setEnvoi(false) }
  }

  return (
    <div className="bs-modale bs-modale--danger">
      <h2 className="bs-modale__titre">Annuler « {s.nom} » ?</h2>
      <ul className="bs-consequences">{lignes.map((l) => <li key={l}>{l}</li>)}</ul>
      <div className="lc-field">
        <label className="lc-label" htmlFor="bs-annuler-motif">Motif (gardé au journal)</label>
        <textarea id="bs-annuler-motif" className="lc-input bs-zone" rows={3} maxLength={500} value={motif} onChange={(e) => setMotif(e.target.value)} />
        <span className="lc-helper">De 3 à 500 caractères.</span>
      </div>
      {erreur ? <p className="bs-note bs-note--ko" role="alert">{erreur}</p> : null}
      <p className="lc-hint bs-identite"><ShieldCheck size={13} aria-hidden="true" /> Gestion vous demandera de confirmer votre identité.</p>
      <div className="bs-modale__actions">
        <button type="button" className="lc-btn lc-btn--ghost" onClick={onClose} disabled={envoi}>Ne pas annuler</button>
        <button type="button" className="lc-btn lc-btn--danger" disabled={!valide || envoi} onClick={() => { void envoyer() }}>
          {envoi ? 'Envoi…' : 'Annuler la soirée'}
        </button>
      </div>
    </div>
  )
}

/* ═══════════════════════ arrêter les ventes ═══════════════════════ */

function Arreter({ ecrivain, onClose }: { ecrivain: Ecrivain; onClose: () => void }) {
  const s = ecrivain.soiree
  // L'écran dit toujours ce qu'il arrête : les trois canaux, cochés, puis à décocher.
  const [canaux, setCanaux] = useState<Canal[]>([...CANAUX])
  const [envoi, setEnvoi] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)
  const lignes = consequencesArret(s, canaux)

  const envoyer = async () => {
    setEnvoi(true); setErreur(null)
    const corps = { version: s.version, canaux: CANAUX.filter((c) => canaux.includes(c)) }
    try {
      const e = await ecrivain.executer({
        ecran: 'soiree:arreter-ventes', corps, envoi: (cle) => gesteSoiree(s.id, 'arreter-ventes', corps, cle),
        // Ce qui a vraiment été retiré : « rien à arrêter » ne se dit pas « ventes arrêtées ».
        succes: succesArret,
      })
      if (e) setErreur(e.message); else onClose()
    } finally { setEnvoi(false) }
  }

  return (
    <div className="bs-modale bs-modale--danger">
      <h2 className="bs-modale__titre">Arrêter les ventes de « {s.nom} » ?</h2>
      <div className="lc-field">
        <span className="lc-label">Canaux à arrêter</span>
        <span className="bs-cases">
          {CANAUX.map((c) => (
            <Checkbox key={c} label={CANAL_LIBELLE[c]} checked={canaux.includes(c)}
              onChange={(on) => setCanaux((x) => (on ? [...x, c] : x.filter((y) => y !== c)))} />
          ))}
        </span>
      </div>
      <ul className="bs-consequences">{lignes.map((l) => <li key={l}>{l}</li>)}</ul>
      <p className="lc-hint">Pour arrêter un seul tarif, sans confirmation d’identité : « Retirer de la vente », dans l’onglet Tarifs.</p>
      {erreur ? <p className="bs-note bs-note--ko" role="alert">{erreur}</p> : null}
      <p className="lc-hint bs-identite"><ShieldCheck size={13} aria-hidden="true" /> Gestion vous demandera de confirmer votre identité.</p>
      <div className="bs-modale__actions">
        <button type="button" className="lc-btn lc-btn--ghost" onClick={onClose} disabled={envoi}>Fermer</button>
        <button type="button" className="lc-btn lc-btn--danger" disabled={canaux.length === 0 || envoi} onClick={() => { void envoyer() }}>
          {envoi ? 'Envoi…' : 'Arrêter les ventes'}
        </button>
      </div>
    </div>
  )
}
